"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DrawCanvas = void 0;
const Block_1 = require("./Block");
const pixi_js_1 = require("pixi.js");
const Global_1 = require("../Global");
const rxjs_1 = require("rxjs");
const lodash_1 = __importDefault(require("lodash"));
const eraser_svg_1 = __importDefault(require("../../assets/svg/eraser.svg"));
const gsap_1 = require("gsap");
const settings_1 = require("../config/settings");
const ColorAdapter_1 = require("../config/ColorAdapter");
const AutoDrawing_1 = require("../objects/AutoDrawing");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class DrawCanvas extends Block_1.Block {
    constructor(app, options) {
        super(app);
        this.options = options;
        this._stroke = new ColorAdapter_1.ColorAdapter(settings_1.Settings.drawColor);
        this._past = [];
        this.initialBounds = { left: Infinity, top: Infinity, right: 0, bottom: 0 };
        this.bounding$ = new rxjs_1.BehaviorSubject(lodash_1.default.clone(this.initialBounds));
        this.strokes$ = new rxjs_1.BehaviorSubject([]);
        this.point$ = new rxjs_1.Subject();
        this.alphaFilter = new pixi_js_1.AlphaFilter(DrawCanvas.OverlayAlpha);
        this.tmatrix = (pixi_js_1.Matrix.IDENTITY).scale((0, Global_1.px)(0.34), (0, Global_1.px)(0.34));
        this.autoDrawings = [];
        this.isdrawing = false;
        this.startDrawing = (evt) => {
            if (this.isEnabled && evt) {
                evt.preventDefault();
                this.picture.addChild(this.makeGraphicsForStroke());
                this.isdrawing = true;
                const pt = this.toPoint(evt);
                this.drawCircle(pt);
                this.strokes$.value.push([[pt.x, pt.y]]);
                this.updateBounds(pt);
                this.point$.next(pt);
                this.strokes$.next(this.strokes$.value);
            }
        };
        this.moveDrawing = (evt) => {
            if (this.isEnabled && evt && this.isdrawing) {
                evt.preventDefault();
                const pt = this.toPoint(evt);
                const curstroke = lodash_1.default.last(this.strokes$.value);
                const lastpt = lodash_1.default.last(curstroke);
                this.drawCircle(pt);
                this.drawLine(pt, lastpt);
                curstroke === null || curstroke === void 0 ? void 0 : curstroke.push([pt.x, pt.y]);
                this.updateBounds(pt);
                this.point$.next(pt);
                this.strokes$.next(this.strokes$.value);
            }
        };
        this.stopDrawing = (evt) => {
            this.isdrawing = false;
            if (this.isEnabled && evt) {
                evt.preventDefault();
            }
        };
        this.options.strokeWidth = this.options.strokeWidth || DrawCanvas.StrokeWidth;
        this.options.borderRadius = this.options.borderRadius || DrawCanvas.BorderRadius;
        this.options.borderWidth = this.options.borderWidth || DrawCanvas.BorderWidth;
        this.backgroundSettings = {
            backgroundColor: settings_1.Settings.canvasColor,
            height: this.options.height,
            width: this.options.width,
            border: this.options.borderWidth,
            radius: this.options.borderRadius
        };
        this.background = this.makeBackground();
        this.background.eventMode = 'static';
        this.background.addEventListener('pointerdown', this.startDrawing);
        this.background.addEventListener('pointermove', this.moveDrawing);
        this.background.addEventListener('pointerup', this.stopDrawing);
        this.background.addEventListener('pointerout', this.stopDrawing);
        this.picture = this.makeDrawingPicture();
        this.eraser = this.makeEraser();
        this.overlaySettings = {
            backgroundColor: settings_1.Settings.whiteColor,
            height: this.options.height,
            width: this.options.width,
            border: 0,
            radius: 0
        };
        this.overlay = this.makeOverlay();
        this.layout();
        this.enabled = !options.disabled;
    }
    addDrawing(drawing, speed = 0) {
        const ad = this.makeAutoDrawingForStroke(drawing, speed);
        ad.pivot.set(ad.width / 2, ad.height / 2);
        this.autoDrawings.push(ad);
        this.picture.addChild(ad);
        this.layout();
        return ad;
    }
    cacheStroke() {
        // Fill a circle a few times with our stroke, just to "warm it up"
        const graphics = this.addChild(this.makeGraphicsForStroke());
        // For some reason here 4 is the "magic number", I have no idea why, it doesn't draw the graphics otherwise
        // Probably something to do with Pixi internals here
        const loc = (0, Global_1.px)(2);
        for (let i = 0; i < 4; i++) {
            graphics.beginFill(this._stroke.color).drawCircle(loc, loc, loc).endFill();
        }
        // Once frame is rendered kill us
        requestAnimationFrame(() => {
            graphics.destroy(true);
        });
    }
    set stroke(stroke) {
        if (stroke !== this._stroke.item) {
            this._past.push(this._stroke);
            this._stroke = new ColorAdapter_1.ColorAdapter(stroke);
            this.cacheStroke();
        }
    }
    get stroke() {
        return this._stroke.item;
    }
    get size() {
        return {
            width: this.options.width,
            height: this.options.height
        };
    }
    get curstroke() {
        if (this.picture.children.length) {
            return this.picture.getChildAt(this.picture.children.length - 1);
        }
        return null;
    }
    resize(width, height) {
        var _a, _b;
        this.backgroundSettings.width = this.overlaySettings.width = this.options.width = width;
        this.backgroundSettings.height = this.overlaySettings.height = this.options.height = height;
        this.makeBox(this.backgroundSettings, this.background);
        this.makeBox(this.overlaySettings, this.overlay);
        if ((_a = this.overlay) === null || _a === void 0 ? void 0 : _a.mask) {
            this.overlay.mask = this.makeMasker(0, this.overlay.mask);
        }
        if ((_b = this.picture) === null || _b === void 0 ? void 0 : _b.mask) {
            this.picture.mask = this.makeMasker(1, this.picture.mask);
        }
    }
    makeOverlay() {
        const overlay = this.makeBox(this.overlaySettings);
        const mask = this.makeMasker();
        overlay.mask = mask;
        overlay.addChild(mask);
        overlay.filters = [this.alphaFilter];
        overlay.visible = false;
        return this.addChild(overlay);
    }
    makeMasker(buffer = 0, to = null) {
        const border = (0, Global_1.px)(this.options.borderWidth);
        const mask = (to ? to.clear() : (new pixi_js_1.Graphics()))
            .beginFill(0xffffff)
            .drawRoundedRect(border + buffer, border + buffer, (0, Global_1.px)(this.options.width) - border * 2 - buffer * 2, (0, Global_1.px)(this.options.height) - border * 2 - buffer * 2, (0, Global_1.px)(this.options.borderRadius))
            .endFill();
        mask.eventMode = 'none';
        return mask;
    }
    makeDrawingPicture() {
        const picture = new pixi_js_1.Container();
        if (this.options.blur) {
            this.blurFilter = new pixi_js_1.BlurFilter((0, Global_1.px)(this.options.blur), 6);
            picture.filters = [this.blurFilter];
        }
        picture.zIndex = 2;
        picture.eventMode = 'none';
        const mask = this.makeMasker(1);
        picture.mask = mask;
        this.addChild(mask);
        return this.addChild(picture);
    }
    makeBackground() {
        const background = this.makeBox(this.backgroundSettings);
        background.zIndex = 0;
        return this.addChild(background);
    }
    inOverlay(color = settings_1.Settings.whiteColor, duration = 0.15) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.overlaySettings.backgroundColor = color;
                this.makeBox(this.overlaySettings, this.overlay);
                this.alphaFilter.alpha = 0.0;
                this.overlay.visible = true;
                yield this.promisedTimeline(gsap_1.gsap.timeline().to(this.alphaFilter, {
                    alpha: DrawCanvas.OverlayAlpha,
                    duration: duration
                }));
            }
            catch (e) {
                console.log(e);
            }
        });
    }
    outOverlay(duration = 0.15) {
        return __awaiter(this, void 0, void 0, function* () {
            const alphaFilter = this.overlay.filters[0];
            this.overlay.visible = true;
            alphaFilter.alpha = DrawCanvas.OverlayAlpha;
            yield this.promisedTimeline(gsap_1.gsap.timeline().to(alphaFilter, {
                alpha: 0.0,
                duration: duration
            }));
            this.overlay.visible = false;
        });
    }
    data() {
        const texture = this.app.renderer.generateTexture(this.picture, {
            width: (0, Global_1.px)(this.options.width),
            height: (0, Global_1.px)(this.options.height)
        });
        const pixels = (this.app.renderer.extract.pixels(texture));
        return new ImageData(pixels, (0, Global_1.px)(this.options.width), (0, Global_1.px)(this.options.height));
    }
    clear() {
        this.picture.removeChildren();
        this.bounding$.next(lodash_1.default.clone(this.initialBounds));
        this.strokes$.next([]);
        this.destroyPast();
    }
    get isEnabled() {
        return this._enabled;
    }
    set enabled(enb) {
        this._enabled = enb;
        if (!this._enabled) {
            this.stopDrawing(null);
            this.eraser.visible = false;
        }
        else {
            this.eraser.visible = true;
        }
    }
    addRelativePoint(pt, atStroke = 0) {
        var _a;
        const ptt = { x: (0, Global_1.px)(this.options.width) * pt[0], y: (0, Global_1.px)(this.options.height) * pt[1] };
        if ((_a = this.strokes$.value[atStroke]) === null || _a === void 0 ? void 0 : _a.length) {
            this.drawCircle(ptt);
            this.drawLine(ptt, lodash_1.default.last(this.strokes$.value[atStroke]));
            this.strokes$.value[atStroke].push([ptt.x, ptt.y]);
        }
        else {
            if (!this.picture.children[atStroke]) {
                // Make sure there is space in the array for our spot
                for (let i = this.picture.children.length; i < atStroke + 1; i++) {
                    this.picture.addChildAt(this.makeGraphicsForStroke(), i);
                }
            }
            this.drawCircle(ptt);
            this.strokes$.value[atStroke] = [[ptt.x, ptt.y]];
        }
        this.updateBounds(ptt);
        this.point$.next(ptt);
        this.strokes$.next(this.strokes$.value);
    }
    drawCircle(pt) {
        if (pt && this.curstroke) {
            this.curstroke.lineStyle(0);
            if (this._stroke.isTexture) { // Assume Texture
                this.curstroke.beginTextureFill({ texture: this._stroke.texture, matrix: this.tmatrix });
            }
            else {
                this.curstroke.beginFill(this._stroke.color);
            }
            const r = (0, Global_1.px)(this.options.strokeWidth / 2);
            this.curstroke.drawCircle(pt.x, pt.y, r).endFill();
        }
    }
    drawLine(pt, lastpt) {
        var _a;
        if (pt && lastpt) {
            const s = {
                width: (0, Global_1.px)(this.options.strokeWidth)
            };
            if (this._stroke.isTexture) {
                s.texture = this._stroke.texture;
                s.matrix = this.tmatrix;
            }
            else {
                s.color = this._stroke.color;
            }
            (_a = this.curstroke) === null || _a === void 0 ? void 0 : _a.lineStyle(s).moveTo(lastpt[0], lastpt[1]).lineTo(pt.x, pt.y);
        }
    }
    toPoint(evt) {
        const ol = (this.app.renderer.view).offsetLeft;
        const ot = (this.app.renderer.view).offsetTop;
        return this.toLocal({ x: (0, Global_1.px)(evt.x - ol), y: (0, Global_1.px)(evt.y - ot) });
    }
    updateBounds(pt) {
        const box = this.bounding$.value;
        let update = false;
        if (pt.x < box.left) {
            box.left = pt.x;
            update = true;
        }
        if (pt.x > box.right) {
            box.right = pt.x;
            update = true;
        }
        if (pt.y < box.top) {
            box.top = pt.y;
            update = true;
        }
        if (pt.y > box.bottom) {
            box.bottom = pt.y;
            update = true;
        }
        if (update) {
            this.bounding$.next(box);
        }
        return update;
    }
    get escale() {
        return (0, Global_1.px)(this.isLandscapeBox ? 0.3 : 0.35);
    }
    makeEraser() {
        const button = this.makeButton(eraser_svg_1.default);
        button.name = 'erasebutton';
        button.scale.set(this.escale);
        this.sigs.push(button.onDown.connect((btn, event) => {
            if (this._enabled) {
                event.stopPropagation();
                button.scale.set(this.escale * 0.95);
                this.clear();
            }
        }));
        this.sigs.push(button.onUp.connect((btn, event) => {
            AssetManager_1.default.eraseSound();
            button.scale.set(this.escale);
        }));
        return this.addChild(button);
    }
    layout() {
        this.eraser.scale.set(this.escale);
        this.eraser.position.set((0, Global_1.px)(this.options.width / 2), (0, Global_1.px)(this.options.height) - (0, Global_1.px)(28));
        this.autoDrawings.forEach((ad) => {
            ad.position.set((0, Global_1.px)(this.options.width / 2), (0, Global_1.px)(this.options.height / 2));
        });
        super.layout();
    }
    destroyPast() {
        this._past.forEach((s) => s.destroy());
        this._past = [];
    }
    destroy(_options) {
        var _a, _b;
        super.destroy(_options);
        this.destroyPast();
        this._stroke.destroy();
        this.background.removeEventListener('pointerdown', this.startDrawing);
        this.background.removeEventListener('pointermove', this.moveDrawing);
        this.background.removeEventListener('pointerup', this.stopDrawing);
        this.background.removeEventListener('pointerout', this.stopDrawing);
        (_a = this.alphaFilter) === null || _a === void 0 ? void 0 : _a.destroy();
        (_b = this.blurFilter) === null || _b === void 0 ? void 0 : _b.destroy();
    }
    makeAutoDrawingForStroke(drawing, speed) {
        const auto = new AutoDrawing_1.AutoDrawing(drawing, speed, this._stroke.color, this.options.strokeWidth);
        return (this.setupG(auto));
    }
    makeGraphicsForStroke() {
        return this.setupG(new pixi_js_1.Graphics());
    }
    setupG(g) {
        const brushShader = this._stroke.brushShader;
        if (brushShader) {
            g.shader = brushShader.shader;
            this._stroke.setResolution(this.width, this.height);
        }
        return g;
    }
}
exports.DrawCanvas = DrawCanvas;
DrawCanvas.OverlayAlpha = 0.9;
DrawCanvas.StrokeWidth = 8;
DrawCanvas.BorderWidth = 3;
DrawCanvas.BorderRadius = 3;
